import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'
import ProductCard from '@/components/shop/product-card'

interface FeaturedProductsProps {
    products: Array<Product>
}

// Staggered like sheets pegged on the drying line
const OFFSETS = ['lg:mt-0', 'lg:mt-24', 'lg:mt-8', 'lg:mt-32']

const FeaturedProducts: FC<FeaturedProductsProps> = ({ products }): ReactNode => {
    return (
        <section id='featured' aria-labelledby='featured-title' className='container-page py-20 md:py-32'>
            <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                <Reveal className='lg:col-span-7'>
                    <p className='flex items-center gap-4 text-sm font-medium text-bark-soft'>
                        <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                        From the drying wall
                    </p>
                    <h2 id='featured-title' className='mt-6 font-heading text-[clamp(2.3rem,5vw,4.4rem)] font-light leading-[1.02] tracking-[-0.02em] text-bark'>
                        Paper for the hand that writes, paints and binds.
                    </h2>
                </Reveal>
                <Reveal delay={120} className='lg:col-span-4 lg:col-start-9 lg:pb-3'>
                    <p className='text-[1.02rem] leading-relaxed text-bark-soft'>
                        Every sheet is a little different in tone and weight. We ship from Samarkand to anywhere in the world, flat and wrapped in our own paper.
                    </p>
                    <Link href='/shop' className='mt-5 inline-flex items-center gap-2 font-medium text-pomegranate'>
                        <span className='ink-link pb-0.5'>See the whole shop</span>
                        <ArrowRight className='size-4' strokeWidth={1.75} />
                    </Link>
                </Reveal>
            </div>

            {products.length > 0 ? (
                <div className='relative mt-16 lg:mt-24'>
                    <svg
                        aria-hidden='true'
                        viewBox='0 0 1200 60'
                        preserveAspectRatio='none'
                        className='absolute -top-8 left-0 hidden h-12 w-full text-bark/30 lg:block'
                    >
                        <path d='M0 6 C 300 46, 900 46, 1200 6' fill='none' stroke='currentColor' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                    </svg>

                    <ul className='grid grid-cols-2 gap-x-5 gap-y-14 sm:gap-x-8 lg:grid-cols-4 lg:gap-x-10'>
                        {products.map((product, index) => (
                            <li key={product.id} className={cn('relative', OFFSETS[index % OFFSETS.length])}>
                                <span aria-hidden='true' className='absolute -top-5 left-1/2 hidden h-6 w-1.5 -translate-x-1/2 rounded-[1px] bg-bark/70 lg:block' />
                                <Reveal delay={index * 120}>
                                    <ProductCard
                                        product={product}
                                        sizes='(min-width: 1024px) 22vw, 46vw'
                                    />
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className='mt-16 rounded-[3px] border border-dashed border-bark/25 px-6 py-14 text-center'>
                    <p className='font-heading text-2xl text-bark'>The drying wall is being restocked.</p>
                    <p className='mt-3 text-bark-soft'>Browse everything we have in the shop.</p>
                    <Link href='/shop' className='mt-6 inline-flex items-center gap-2 font-medium text-pomegranate'>
                        <span className='ink-link pb-0.5'>Go to the shop</span>
                        <ArrowRight className='size-4' strokeWidth={1.75} />
                    </Link>
                </div>
            )}
        </section>
    )
}

export default FeaturedProducts
